import { MGMAuthError } from '@mgmresorts/mgm-auth-js-sdk';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { KnownError, UNKNOWN_ERROR_MESSAGE } from '../../../api/errors';
import { MlifeActivationRequest } from '../../../api/fetch/mlife-activation';
import { mlifeAccountSetupRequest } from '../../../api/fetch/mlife-activation/mlife-account-setup';
import { useAppRedirect } from '../../../hooks/useAppRedirect';
import { BindCheckbox, useCheckbox } from '../../../hooks/useCheckbox';
import { useFieldValidation } from '../../../hooks/useFieldValidation';
import { BindInput, useInput } from '../../../hooks/useInput';
import { usePasswordInput } from '../../../hooks/usePasswordInput';
import { BindSelect, useSelect } from '../../../hooks/useSelect';
import { captureEvent, captureException, captureMessage } from '../../../monitoring/logrocket';
import { useEvent } from '../../../utils/analytics/useEvent';
import { AuthContext } from '../../../utils/auth/AuthProvider';
import { ClientId, MaxLengths } from '../../../utils/constants';
import { getFeaturesByClientId } from '../../../utils/getFeaturesByClientId';
import { usePersistentParameters } from '../../../utils/persistParameterContext';
import { redirectToSignIn } from '../../../utils/redirect';
import { safeParsePhoneNumber, safePhoneNumber } from '../../../utils/safePhoneNumber';
import { addressValidator, cityValidator, passwordValidator, phoneNumberValidator } from '../../../utils/validators';

export type Context = {
  password: string;
  passwordValidations: { [key: string]: boolean };
  bindPassword: BindInput;
  isValidPassword: boolean;

  phoneNumberData?: string;
  phoneNumber: string;
  bindPhoneNumber: BindInput;

  addressData?: { [key: string]: string };
  address: string;
  address2: string;
  country: string | string[];
  state: string | string[];
  city: string;
  customState: string;
  zip: string;

  bindAddress: BindInput;
  bindAddress2: BindInput;
  bindCountry: BindSelect;
  bindState: BindSelect;
  resetState: () => void;
  bindCity: BindInput;
  bindCustomState: BindInput;
  resetCustomState: () => void;
  bindZip: BindInput;

  policy: boolean;
  bindPolicy: BindCheckbox;

  canadianOptIn: boolean;
  bindCanadianOptIn: BindCheckbox;

  invalidFields: string[];
  isLoading: boolean;
  isCanadian: boolean;
  canProcessAccountSetup: boolean;
  processAccountSetup: () => void;
  accountSetupError: string | null;
  creditAccount?: boolean;
  showAccountActivatedScreen: boolean;
};

export const MlifeAccountSetupContext = createContext<Context>({} as Context);

export const MlifeAccountSetupContextProvider = ({
  children,
  activationRequest,
}: {
  children?: React.ReactNode;
  activationRequest: MlifeActivationRequest;
}) => {
  const trackEvent = useEvent();
  const redirectToApp = useAppRedirect();
  const [accountSetupError, setAccountSetupError] = useState<null | string>(null);
  const [isLoading, setLoadingState] = useState(false);
  const [showAccountActivatedScreen, setShowAccountActivatedScreen] = useState(false);
  const {
    fName: firstName,
    lName: lastName,
    email: login,
    address: addressData,
    phoneNumber: phoneNumberData,
    creditAccount,
  } = activationRequest;
  const { validateField, invalidFields } = useFieldValidation([
    passwordValidator,
    phoneNumberValidator,
    addressValidator,
    cityValidator,
  ]);

  const {
    value: password,
    bind: bindPassword,
    isValid: isValidPassword,
    validations: passwordValidations,
  } = usePasswordInput('', 'password', undefined, validateField);

  const { value: address, bind: bindAddress } = useInput(
    addressData?.street1 ?? '',
    'address',
    undefined,
    validateField,
  );
  const { value: address2, bind: bindAddress2 } = useInput(addressData?.street2 ?? '', 'address2');
  const { value: country, bind: bindCountry } = useSelect(addressData?.country ?? '', 'country');
  const { value: state, bind: bindState, reset: resetState } = useSelect(addressData?.state ?? '', 'state');
  const { value: city, bind: bindCity } = useInput(addressData?.city ?? '', 'city', undefined, validateField);
  const {
    value: customState,
    bind: bindCustomState,
    reset: resetCustomState,
  } = useInput(addressData?.state ?? '', 'customState');
  const { value: zip, bind: bindZip } = useInput(addressData?.zipCode ?? '', 'zip');

  const { value: phoneNumber, bind: bindPhoneNumber } = useInput(
    safeParsePhoneNumber(phoneNumberData ?? ''),
    'phoneNumber',
    validateField,
  );
  const { value: policy, bind: bindPolicy } = useCheckbox(false, 'policy');
  const { value: canadianOptIn, bind: bindCanadianOptIn } = useCheckbox(false, 'canadianOptIn');
  const { params } = usePersistentParameters();
  const { isAllowOktaSignInWithRedirect } = getFeaturesByClientId(params.client_id as ClientId);

  const { authClient } = useContext(AuthContext);

  const isCanadian = useMemo(() => {
    if (login?.endsWith('.ca')) {
      return true;
    }

    if ((country as string)?.toLowerCase() === 'ca') {
      return true;
    }

    return false;
  }, [country, login]);

  const canProcessAccountSetup = useMemo(() => {
    return !!(
      password &&
      firstName &&
      lastName &&
      policy &&
      isValidPassword &&
      city &&
      country &&
      address &&
      address.length <= MaxLengths.Address &&
      (state || customState) &&
      (!['US', 'CA'].includes(country as string) || !!zip) &&
      !invalidFields.length
    );
  }, [
    address,
    city,
    country,
    customState,
    firstName,
    invalidFields.length,
    isValidPassword,
    lastName,
    password,
    policy,
    state,
    zip,
  ]);

  const processSignIn = useCallback(
    async (username, password) => {
      try {
        setLoadingState(true);

        const authResponse = await authClient.signIn({
          username,
          password,
        });

        if (authResponse instanceof MGMAuthError) {
          redirectToSignIn();
        } else {
          /**
           * TODO: Implement cleaner solution
           * For third-party vendors (e.g. BetMGM, Cvent's Passkey, HGS) Okta will handle redirect using signInWithRedirect method in AuthSDK
           */
          if (isAllowOktaSignInWithRedirect) {
            return;
          }

          redirectToApp();
        }
      } catch (err) {
        redirectToSignIn();
      } finally {
        setLoadingState(false);
      }
    },
    [authClient, isAllowOktaSignInWithRedirect, redirectToApp],
  );

  const processAccountSetup = useCallback(async () => {
    setLoadingState(true);

    const partnerData =
      params.partner && params.tokenKey
        ? { name: params.partner as string, tokenKey: params.tokenKey as string }
        : undefined;

    const requestBody = {
      firstName: activationRequest.fName ?? '',
      lastName: activationRequest.lName ?? '',
      dob: activationRequest.dateOfBirth ?? '',
      login: activationRequest.login,
      primaryPhone: safePhoneNumber(phoneNumber),
      activationKid: activationRequest.activationKid,
      canadianOptIn: isCanadian ? canadianOptIn : true,
      partner: partnerData,
      credentials: {
        password,
      },
      address: {
        street: address,
        street2: address2,
        city: city,
        state: customState || (state as string),
        postalCode: zip,
        country: country as string,
      },
    };

    try {
      if (accountSetupError) {
        setAccountSetupError(null);
      }

      captureEvent('Activation: Setup', {
        email: String(activationRequest.login),
      });

      await mlifeAccountSetupRequest(requestBody);

      trackEvent(
        'identity – m life - account setup submit success',
        'm life account setup submit success',
        'mlifeaccountsetupsuccess',
        {
          registerState: 'success',
          birthYear: new Date(activationRequest.birthday).getFullYear().toString(),
        },
      );
      captureMessage('user account activation completed', 'activate', { username: activationRequest.login });
      setShowAccountActivatedScreen(true);
      await processSignIn(activationRequest.login, password);
    } catch (error) {
      let message;
      if (error instanceof KnownError) {
        message = error.message;
      } else {
        message = UNKNOWN_ERROR_MESSAGE;
      }
      setAccountSetupError(message);

      trackEvent(
        'identity – m life - account setup submit error',
        'm life account setup submit error',
        'mlifeaccountsetupfail',
        {
          registerState: 'failed',
          errorDisplayed: message,
        },
      );

      captureException(error as Error, 'activate');
    } finally {
      setLoadingState(false);
    }
  }, [
    params.partner,
    params.tokenKey,
    activationRequest.fName,
    activationRequest.lName,
    activationRequest.dateOfBirth,
    activationRequest.login,
    activationRequest.activationKid,
    activationRequest.birthday,
    phoneNumber,
    isCanadian,
    canadianOptIn,
    password,
    address,
    address2,
    city,
    customState,
    state,
    zip,
    country,
    accountSetupError,
    trackEvent,
    processSignIn,
  ]);

  const mlifeAccountSetup = useMemo(() => {
    return {
      password,
      bindPassword,
      isValidPassword,
      passwordValidations,

      phoneNumberData,
      phoneNumber,
      bindPhoneNumber,

      addressData,
      address,
      bindAddress,

      address2,
      bindAddress2,

      country,
      bindCountry,

      state,
      bindState,
      city,
      bindCity,

      customState,
      bindCustomState,

      resetState,
      resetCustomState,

      zip,
      bindZip,

      policy,
      bindPolicy,

      canadianOptIn,
      bindCanadianOptIn,

      invalidFields,
      isLoading,
      isCanadian,
      canProcessAccountSetup,
      processAccountSetup,
      accountSetupError,
      creditAccount,
      showAccountActivatedScreen,
    };
  }, [
    password,
    bindPassword,
    isValidPassword,
    passwordValidations,

    phoneNumberData,
    phoneNumber,
    bindPhoneNumber,

    addressData,
    address,
    bindAddress,

    address2,
    bindAddress2,

    country,
    bindCountry,

    state,
    bindState,
    city,
    bindCity,

    customState,
    bindCustomState,

    resetState,
    resetCustomState,

    zip,
    bindZip,

    policy,
    bindPolicy,

    canadianOptIn,
    bindCanadianOptIn,

    invalidFields,
    isLoading,
    isCanadian,
    canProcessAccountSetup,
    processAccountSetup,
    accountSetupError,
    creditAccount,
    showAccountActivatedScreen,
  ]);
  return <MlifeAccountSetupContext.Provider value={mlifeAccountSetup}>{children}</MlifeAccountSetupContext.Provider>;
};
